import { createSelector } from '@reduxjs/toolkit';
import { omit } from 'lodash-es';
import { useMemo } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';

import HabitatLink, { HabitatLinkProps } from '@/components/HabitatLink';
import { buildHabitatURL } from '@/components/HabitatLink/utils';
import HabitatsGrid from '@/components/HabitatsGrid';
import { setProductsPromptModalState } from '@/components/products/ProductsPromptModal/redux/actions';
import { getDefaultProductsPromptModalState } from '@/components/products/ProductsPromptModal/redux/selectors';
import { toProductPromptType } from '@/components/products/ProductsPromptModal/utils';
import { sortHabitats } from '@/helpers/habitatsSorter';
import { AppState, useSelector, useStore } from '@/redux/helper';

const selectSortedHabitats = createSelector(
  (state: AppState) => state.allHabitats,
  (habitats) => sortHabitats(habitats, ['online', 'currentViewers', 'index', 'id'], 0),
);

const Habitats = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const allHabitats = useSelector(selectSortedHabitats);
  const favoriteHabitatIds = useSelector((state) => state.user.favoriteHabitats);
  const favoriteHabitats = useMemo(() => {
    const favoriteHabitatsSet = new Set(favoriteHabitatIds);
    return allHabitats.filter((habitat) => favoriteHabitatsSet.has(habitat._id));
  }, [allHabitats, favoriteHabitatIds]);

  const recommendedHabitats = useMemo(() => {
    const onlineHabitats = allHabitats.filter((habitat) => habitat.isStreamOn);
    const favoriteHabitatsSet = new Set(favoriteHabitatIds);
    const onlineButNotFavoriteHabitats = onlineHabitats.filter((habitat) => !favoriteHabitatsSet.has(habitat._id));
    if (onlineButNotFavoriteHabitats.length > 0) {
      return onlineButNotFavoriteHabitats;
    }
    if (onlineHabitats.length > 0) {
      return onlineHabitats;
    }
    return allHabitats.slice(0, 10);
  }, [allHabitats, favoriteHabitatIds]);

  const [{ matches }] = useRouter();
  const parsedProductPromptType = toProductPromptType(matches?.ppt?.toLowerCase());

  const renderLink = (props: HabitatLinkProps) => {
    const filteredProps = parsedProductPromptType
      ? omit(props, ['productsPromptModalState', 'zooSlug', 'slug', 'query', 'habitatId', 'children'])
      : props;
    return parsedProductPromptType ? (
      <a
        data-native
        href={buildHabitatURL(props.zooSlug, props.slug, `ppt=${parsedProductPromptType}`)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...filteredProps}
        onClick={(e) => {
          e.preventDefault();
          const defaultState = getDefaultProductsPromptModalState(
            parsedProductPromptType,
            props.habitatId,
            store.getState(),
          );
          dispatch(
            setProductsPromptModalState({ ...defaultState, resetQueryParamsOnDismiss: false, entryPoint: 'habitat-link' }),
          );
        }}
      >
        Visit Habitat
      </a>
    ) : (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <HabitatLink {...props} />
    );
  };

  return (
    <div className="grow">
      {favoriteHabitats.length > 0 ? (
        <HabitatsGrid
          className="gap-6 bg-secondary-green px-6 pb-9 pt-6 lg:px-24 lg:py-14 lg:pt-10 2xl:py-20"
          habitats={favoriteHabitats}
          renderLink={renderLink}
        />
      ) : (
        <div className="h-9 bg-secondary-green lg:h-10" />
      )}
      <div className="-mt-4 grow rounded-t-2xl bg-white px-3 pb-9 pt-4 lg:px-24 lg:py-12">
        <h2 className="preflight preflight-h2 mx-1 mb-4 text-base font-bold text-black lg:mb-7 lg:text-xl">Recommended</h2>
        <HabitatsGrid habitats={recommendedHabitats} renderLink={renderLink} />
      </div>
    </div>
  );
};

export default Habitats;
